/**
 * Omniapps Account Service Integration
 * Handles authentication with Omniapps Account API
 */

const ACCOUNT_API_URL = import.meta.env.VITE_ACCOUNT_SERVICE_URL || 'https://account.omniapps.net';

// Token storage keys
const ACCESS_TOKEN_KEY = 'omniapps_idea_access_token';
const REFRESH_TOKEN_KEY = 'omniapps_idea_refresh_token';
const USER_KEY = 'omniapps_idea_user';

class AccountService {
  constructor() {
    this.accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    this.refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    this.user = this.loadUser();
  }

  loadUser() {
    const userStr = localStorage.getItem(USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  }

  saveAuth(accessToken, refreshToken, user) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.user = user;
    
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  clearAuth() {
    this.accessToken = null;
    this.refreshToken = null;
    this.user = null;
    
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  isAuthenticated() {
    return !!this.accessToken && !!this.refreshToken;
  }

  getCurrentUser() {
    return this.user;
  }

  getAccessToken() {
    return this.accessToken;
  }

  async authenticatedFetch(url, options = {}) {
    const method = (options.method || 'GET').toUpperCase();

    const makeRequest = async (token) => {
      const response = await fetch(url, {
        ...options,
        cache: options.cache ?? (method === 'GET' ? 'no-store' : options.cache),
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response;
    };

    let response = await makeRequest(this.accessToken);

    if (response.status === 401 && this.refreshToken) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        response = await makeRequest(this.accessToken);
      } else {
        this.clearAuth();
        window.location.href = '/login';
        throw new Error('Session expired');
      }
    }

    return response;
  }

  async login(email, password) {
    const response = await fetch(`${ACCOUNT_API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Login failed');
    }

    this.saveAuth(data.accessToken, data.refreshToken, data.user);
    return data.user;
  }

  async register(email, password, firstName, lastName) {
    const response = await fetch(`${ACCOUNT_API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, firstName, lastName }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Registration failed');
    }

    return data;
  }

  async logout() {
    if (this.refreshToken) {
      try {
        await fetch(`${ACCOUNT_API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: this.refreshToken }),
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    
    this.clearAuth();
  }

  async refreshAccessToken() {
    if (!this.refreshToken) {
      return false;
    }

    try {
      const response = await fetch(`${ACCOUNT_API_URL}/api/auth/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      this.accessToken = data.accessToken;
      localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
      
      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  }

  async validateToken() {
    try {
      const response = await this.authenticatedFetch(`${ACCOUNT_API_URL}/api/auth/me`);
      
      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      this.user = data.user;
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      
      return data.user;
    } catch (error) {
      console.error('Token validation error:', error);
      return null;
    }
  }

  async requestPasswordReset(email) {
    const response = await fetch(`${ACCOUNT_API_URL}/api/auth/request-password-reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Password reset request failed');
    }

    return data;
  }
}

export default new AccountService();
